/**
 * session-idle (session-watch-rightrail — 2026-07-24) — 클라이언트측 60분 idle 세션 **싱글턴 엔진**.
 *
 * 기존 `NavBar/SessionTimer` 안에 있던 로직을 그대로 옮긴 것이다. 세션 시계를 좌측 바가 아니라
 * 우측 레일(프로필 옆)에서 그리게 되면서, "표시 위치"와 "세션 만료 처리"를 분리해야 했다:
 *   - 표시(SessionWatch)는 레일이 있는 화면에서만 렌더된다.
 *   - 만료 처리(자동 로그아웃)는 화면과 무관하게 항상 돌아야 한다 → AppLayout 이 SessionIdleGuard 로 상시 구독.
 * 두 구독자가 붙어도 타이머·리스너는 **하나만** 돈다(구독 카운트 기반 기동/정지).
 *
 * 동작(기존과 동일):
 *   - 60분 무활동 → 자동 로그아웃(서버 /auth/logout 으로 쿠키·refresh 토큰 무효화 + clearSession)
 *   - 사용자 활동(마우스/키보드/터치/스크롤) 시 슬라이딩(60분 리셋)
 *   - lastActivity 를 localStorage 로 공유 → 멀티탭 안전(한 탭이라도 활동하면 유지)
 */
import { useSyncExternalStore } from 'react';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';

/** 세션 유지 시간(무활동 기준) — 기본값. */
export const SESSION_IDLE_MS = 60 * 60 * 1000;

const ACTIVITY_KEY = 'axi.session.lastActivity';
const WRITE_THROTTLE_MS = 5_000;           // localStorage 쓰기 throttle

const subscribers = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let startedAt = 0;
let lastWrite = 0;
// 만료를 이미 처리했는가(로그아웃했거나 화면잠금이 가로챘거나) — 1초마다 재발화 방지.
let loggedOut = false;
let remaining = Math.floor(SESSION_IDLE_MS / 1000);

function readLastActivity(fallback: number): number {
  try {
    const v = Number(localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(v) && v > 0 ? v : fallback;
  } catch {
    return fallback;
  }
}

function writeActivity(): void {
  const now = Date.now();
  if (now - lastWrite < WRITE_THROTTLE_MS) return;
  lastWrite = now;
  try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* ignore */ }
}

function tick(): void {
  const last = readLastActivity(startedAt);
  const next = Math.ceil((last + idleMs - Date.now()) / 1000);
  if (next !== remaining) {
    remaining = next;
    for (const fn of subscribers) fn();
  }
  if (next <= 0 && !loggedOut) {
    loggedOut = true;
    // 화면잠금이 켜져 있으면 여기서 가로채 화면만 덮는다(로그아웃은 잠금 화면이 맡는다).
    if (onExpire?.()) return;
    // 서버 세션 무효화(쿠키·refresh 토큰) 후 클라이언트 정리 → AuthGuard 가 /login 으로.
    apiClient.post('/auth/logout').catch(() => { /* best-effort */ })
      .finally(() => { useAuthStore.getState().clearSession(); });
  }
}

/**
 * 실제로 쓰는 무활동 한도. 기본은 위 상수 그대로다.
 *
 * screen-lock (2026-08-19) — 화면잠금이 켜지면 사용자가 고른 대기 시간(5분~4시간)이 곧
 * 이 값이 된다. 우측 레일 세션 시계도 같은 값을 기준으로 그려야 "0 이 되면 잠긴다" 는 말이
 * 맞는다. 화면잠금을 끄면 다시 기본 60분으로 돌아온다(예전 동작 그대로).
 */
let idleMs: number = SESSION_IDLE_MS;

/** 현재 적용 중인 무활동 한도(ms). */
export function getSessionIdleMs(): number {
  return idleMs;
}

/** 무활동 한도 변경 — 1분~24시간으로 제한. 값이 바뀌면 즉시 구독자에게 반영한다. */
export function setSessionIdleMs(ms: number): void {
  const clamped = Math.min(Math.max(Math.round(ms), 60_000), 24 * 60 * 60 * 1000);
  if (clamped === idleMs) return;
  idleMs = clamped;
  if (timer !== null) tick();
}

/**
 * 만료 시 동작 가로채기 — 기본(null)은 지금까지처럼 자동 로그아웃.
 *
 * 화면잠금이 켜져 있으면 잠금 호스트가 여기에 핸들러를 걸어 **로그아웃 대신 화면을 덮는다**.
 * 핸들러가 true 를 돌려주면 로그아웃하지 않는다(잠금 화면이 그 뒤의 로그아웃 시한을 맡는다).
 */
type ExpiryHandler = () => boolean;
let onExpire: ExpiryHandler | null = null;

export function setSessionExpiryHandler(fn: ExpiryHandler | null): void {
  onExpire = fn;
}

/**
 * 무활동 타이머 되감기 — 잠금 해제 직후처럼 "지금부터 다시 센다" 가 필요할 때.
 * 만료 처리 플래그도 함께 풀어, 다음 만료가 정상적으로 다시 일어나게 한다.
 */
export function resetSessionIdle(): void {
  const now = Date.now();
  lastWrite = now;
  try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* ignore */ }
  loggedOut = false;
  if (timer !== null) tick();
}

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

function start(): void {
  if (timer !== null) return;
  startedAt = Date.now();
  loggedOut = false;
  // 기동 = 활동으로 간주(새 탭/새로고침 시 60분 리셋).
  try { localStorage.setItem(ACTIVITY_KEY, String(startedAt)); } catch { /* ignore */ }
  const opts = { passive: true } as const;
  for (const ev of ACTIVITY_EVENTS) window.addEventListener(ev, writeActivity, opts);
  tick();
  timer = setInterval(tick, 1000);
}

function stop(): void {
  if (timer === null) return;
  clearInterval(timer);
  timer = null;
  for (const ev of ACTIVITY_EVENTS) window.removeEventListener(ev, writeActivity);
}

/** useSyncExternalStore 용 — 첫 구독에서 엔진 기동, 마지막 해지에서 정지. */
export function subscribeSessionIdle(onChange: () => void): () => void {
  start();
  subscribers.add(onChange);
  return () => {
    subscribers.delete(onChange);
    if (subscribers.size === 0) stop();
  };
}

/** 남은 초(음수 가능 — 만료 판정용). */
export function getSessionRemaining(): number {
  return remaining;
}

/** mm:ss */
export function formatSessionTime(sec: number): string {
  const s = Math.max(0, sec);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export type SessionIdleState = 'ok' | 'warn' | 'danger' | 'expired';

/**
 * 세션 잔여 구독 훅 — 남은 초 · 잔여율(%) · 상태(색 규칙 SSOT).
 * 상태 임계값은 기존 SessionTimer 와 동일(2분 이하 danger / 10분 이하 warn).
 */
export function useSessionIdle(): { remaining: number; pct: number; state: SessionIdleState } {
  const raw = useSyncExternalStore(subscribeSessionIdle, getSessionRemaining, getSessionRemaining);
  const rem = Math.max(0, raw);
  const pct = Math.max(0, Math.min(100, (rem / (idleMs / 1000)) * 100));
  const state: SessionIdleState = raw <= 0 ? 'expired' : rem <= 120 ? 'danger' : rem <= 600 ? 'warn' : 'ok';
  return { remaining: rem, pct, state };
}
